import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';

const BLUE  = 'var(--signal)';
const TEXT1 = 'var(--ink)';
const TEXT2 = 'var(--slate)';
const TEXT3 = 'var(--mute)';
const GREEN = 'var(--go)';

const CATEGORIES = [
  'Vehicle and transportation',
  'Tools and equipment',
  'Insurance',
  'Phone and communications',
  'Protective gear and uniforms',
  'Training and licensing',
  'Marketing and advertising',
  'Software and subscriptions',
  'Office and admin',
  'Materials and supplies',
  'Travel and lodging',
  'Custom',
];

const FREQUENCIES = [
  { label: 'Monthly',  value: 'monthly' },
  { label: 'One-time', value: 'one_time' },
];

function StickyHeader() {
  return (
    <div style={{
      position: 'sticky', top: 0, zIndex: 100,
      height: 56, backgroundColor: 'var(--card)',
      borderBottom: '0.5px solid rgba(0,0,0,0.12)',
      display: 'flex', alignItems: 'center',
      padding: '0 20px',
    }}>
      <span style={{ fontFamily: 'var(--font-serif)', fontSize: '1.25rem', fontWeight: 700, letterSpacing: '-0.3px' }}>
        <span style={{ color: TEXT1 }}>Tru</span>
        <span style={{ color: BLUE }}>Flow</span>
      </span>
    </div>
  );
}

function ProgressBar({ pct }) {
  return (
    <div style={{ height: 3, backgroundColor: 'rgba(0,0,0,0.07)' }}>
      <div style={{ height: '100%', width: `${pct}%`, backgroundColor: BLUE, transition: 'width 0.3s ease' }} />
    </div>
  );
}

function FocusInput({ value, onChange, placeholder, type = 'text' }) {
  const [focused, setFocused] = useState(false);
  return (
    <input
      type={type}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
      style={{
        width: '100%', height: 44,
        border: `0.5px solid ${focused ? BLUE : 'rgba(0,0,0,0.18)'}`,
        borderRadius: 8, padding: '0 12px',
        fontSize: 14, color: TEXT1,
        backgroundColor: 'var(--card)', outline: 'none',
        boxSizing: 'border-box',
      }}
    />
  );
}

function FocusSelect({ value, onChange, children }) {
  const [focused, setFocused] = useState(false);
  return (
    <select
      value={value}
      onChange={onChange}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
      style={{
        width: '100%', height: 44,
        border: `0.5px solid ${focused ? BLUE : 'rgba(0,0,0,0.18)'}`,
        borderRadius: 8, padding: '0 12px',
        fontSize: 14, color: value ? TEXT1 : TEXT3,
        backgroundColor: 'var(--card)', outline: 'none',
        boxSizing: 'border-box',
        appearance: 'none',
        backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%236b7280' d='M6 8L1 3h10z'/%3E%3C/svg%3E")`,
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'right 12px center',
        cursor: 'pointer',
      }}
    >
      {children}
    </select>
  );
}

function AmountInput({ value, onChange }) {
  const [focused, setFocused] = useState(false);
  return (
    <div style={{ position: 'relative' }}>
      <span style={{
        position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)',
        fontSize: 14, color: TEXT2,
        pointerEvents: 'none',
      }}>
        $
      </span>
      <input
        type="number"
        value={value}
        onChange={onChange}
        placeholder="e.g. 800"
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        style={{
          width: '100%', height: 44,
          border: `0.5px solid ${focused ? BLUE : 'rgba(0,0,0,0.18)'}`,
          borderRadius: 8, padding: '0 12px 0 24px',
          fontSize: 14, color: TEXT1,
          backgroundColor: 'var(--card)', outline: 'none',
          boxSizing: 'border-box',
        }}
      />
    </div>
  );
}

function Field({ label, optional, children }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: 4 }}>
        <span style={{ fontSize: 11, fontWeight: 600, color: TEXT1 }}>{label}</span>
        {optional && <span style={{ fontSize: 10, color: TEXT3 }}>Optional</span>}
      </div>
      {children}
    </div>
  );
}

export default function OnboardingExpenses() {
  const navigate = useNavigate();
  const [name, setName]           = useState('');
  const [category, setCategory]   = useState('');
  const [amount, setAmount]       = useState('');
  const [frequency, setFrequency] = useState('');
  const [error, setError]         = useState('');
  const [loading, setLoading]     = useState(false);
  const [saved, setSaved]         = useState([]);

  // Rows already in the database, so a refresh mid-step doesn't present an
  // empty list and invite the user to re-add what they already saved — POST
  // has no idempotency key, so a re-add is a genuine second row, and
  // _sync_business_expenses recomputes TaxSetup.business_expenses from the
  // sum of every row on each write, carrying the duplicate straight into the
  // reserve.
  //
  // GET /expenses is require_active_access while POST is
  // allow_onboarding_access, so this 402s for anyone still unsubscribed at
  // this step. That is caught and ignored on purpose: the list simply stays
  // as it was, which is exactly today's behaviour. It must NOT follow the
  // usual "402 → /pricing" rule — onboarding sits in front of the paywall,
  // and bouncing out of it here would break the flow it belongs to.
  useEffect(() => {
    let cancelled = false;
    async function loadExisting() {
      try {
        const res = await api.get('/expenses');
        if (cancelled) return;
        // Server orders newest-first; reverse so the oldest reads at the top
        // and newly added rows keep appending to the bottom.
        setSaved([...(res.data?.expenses ?? [])].reverse());
      } catch {
        // 402 (not subscribed yet) or a network blip. Either way the step
        // still works — this only ever adds context, never gates the form.
      }
    }
    loadExisting();
    return () => { cancelled = true; };
  }, []);

  async function handleAdd(e) {
    e.preventDefault();
    if (!name || !category || !amount || !frequency) {
      setError('Please fill in all required fields.');
      return;
    }
    setError('');
    setLoading(true);
    try {
      const res = await api.post('/expenses', {
        name,
        category,
        amount: parseFloat(amount),
        frequency,
        notes: null,
        date: null,
      });
      setSaved((prev) => [...prev, res.data]);
      setName('');
      setCategory('');
      setAmount('');
      setFrequency('');
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Could not save. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  function fmtAmount(n) {
    return '$' + Number(n).toLocaleString();
  }

  return (
    <div style={{ backgroundColor: 'var(--paper)', minHeight: '100vh' }}>
      <StickyHeader />
      <ProgressBar pct={100} />

      <div style={{ maxWidth: 480, margin: '0 auto', padding: '20px 16px 48px' }}>
        <p style={{ margin: '0 0 4px', fontSize: 11, color: TEXT3 }}>Step 4 of 4</p>
        <h1 style={{ margin: '0 0 8px', fontFamily: 'var(--font-serif)', fontSize: 22, fontWeight: 700, color: TEXT1 }}>
          Add your business expenses
        </h1>
        <p style={{ margin: '0 0 24px', fontSize: 13, color: TEXT2, lineHeight: 1.6 }}>
          Truck payment, fuel, insurance, phone — anything you spend to do your job. You can add more later.
        </p>

        {saved.length > 0 && (
          <div style={{
            backgroundColor: 'var(--card)',
            boxShadow: 'var(--sh-card)',
            borderRadius: 'var(--r-card)', padding: '4px 16px', marginBottom: 16,
          }}>
            {saved.map((exp, i) => (
              <div key={exp.id} style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                padding: '10px 0',
                borderBottom: i < saved.length - 1 ? '0.5px solid rgba(0,0,0,0.07)' : 'none',
              }}>
                <div>
                  <span style={{ fontSize: 14, color: TEXT1 }}>{exp.name}</span>
                  <span style={{ fontSize: 11, color: TEXT3, marginLeft: 8 }}>
                    {exp.frequency === 'monthly' ? 'Monthly' : 'One-time'}
                  </span>
                </div>
                <span style={{ fontSize: 14, fontWeight: 600, color: GREEN }}>
                  {fmtAmount(exp.amount)} ✓
                </span>
              </div>
            ))}
          </div>
        )}

        <div style={{
          backgroundColor: 'var(--card)',
          boxShadow: 'var(--sh-card)',
          borderRadius: 'var(--r-card)', padding: 16, marginBottom: 16,
        }}>
          <form onSubmit={handleAdd} noValidate>
            <Field label="Expense name">
              <FocusInput
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Truck payment, Fuel, Insurance"
              />
            </Field>

            <Field label="Category">
              <FocusSelect value={category} onChange={(e) => setCategory(e.target.value)}>
                <option value="" disabled>Select a category…</option>
                {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </FocusSelect>
            </Field>

            <Field label="Amount">
              <AmountInput value={amount} onChange={(e) => setAmount(e.target.value)} />
            </Field>

            <Field label="Frequency">
              <FocusSelect value={frequency} onChange={(e) => setFrequency(e.target.value)}>
                <option value="" disabled>Select frequency…</option>
                {FREQUENCIES.map(({ label, value }) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </FocusSelect>
            </Field>

            {error && (
              <p style={{
                color: 'var(--stop)', fontSize: 13,
                margin: '0 0 12px', padding: '10px 12px',
                backgroundColor: 'var(--stop-soft)', border: '1px solid var(--stop-border)',
                borderRadius: 8,
              }}>
                {error}
              </p>
            )}

            <button
              type="submit"
              disabled={loading}
              style={{
                width: '100%',
                backgroundColor: loading ? 'var(--signal-disabled)' : BLUE,
                color: 'var(--on-signal)', borderRadius: 10,
                padding: 13, fontSize: 14, fontWeight: 500,
                border: 'none', cursor: loading ? 'default' : 'pointer',
              }}
            >
              {loading ? 'Saving…' : saved.length > 0 ? '+ Add another expense' : 'Add expense'}
            </button>
          </form>
        </div>

        <button
          onClick={() => navigate('/onboarding/summary')}
          style={{
            width: '100%',
            backgroundColor: saved.length > 0 ? BLUE : 'var(--signal-disabled)',
            color: 'var(--on-signal)', borderRadius: 10,
            padding: 13, fontSize: 14, fontWeight: 600,
            border: 'none', cursor: 'pointer',
            marginBottom: 16,
          }}
        >
          See my numbers
        </button>

        <div style={{ textAlign: 'center' }}>
          <button
            onClick={() => navigate('/onboarding/summary')}
            style={{
              background: 'none', border: 'none',
              color: TEXT3, fontSize: 13,
              cursor: 'pointer',
              textDecoration: 'underline',
            }}
          >
            Skip for now
          </button>
        </div>
      </div>
    </div>
  );
}
